import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertTriangle,
  Bell,
  Building2,
  CheckCircle2,
  CreditCard,
  FileCheck2,
  Image,
  Loader2,
  Palette,
  Pencil,
  Receipt,
  Shield,
  Upload,
  User,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { PAYROLL_DAY_OPTIONS, PAYROLL_FREQUENCY_OPTIONS } from '../../config/tenantSetupOptions';
import { getPortalAccessSummary } from '../../config/portalNavigation';
import MfaSecurityCard from '../../components/portal/MfaSecurityCard';

const API = API_BASE;

const PLAN_LABELS = {
  essential: 'Essential',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const ROLE_LABELS = {
  super_admin: 'Workspace Owner',
  admin: 'Admin',
  branch_manager: 'Branch Manager',
  manager: 'Manager',
  auditor: 'Auditor',
};

const CAPABILITY_LABELS = {
  tenant_access: 'Tenant access',
  official_documents: 'Documents and PDFs',
  worker_onboarding: 'Worker onboarding',
  payroll: 'Payroll',
  invoicing: 'Invoicing',
  billing: 'Billing',
};

const FEATURE_LABELS = {
  worker_readiness: 'Worker readiness',
  worker_evidence_tracking: 'Worker evidence tracking',
  document_tracking: 'Document tracking',
  rtw_dbs_training_evidence_status: 'RTW, DBS and training status',
  service_user_readiness_basics: 'Service user readiness basics',
  shift_assignment_safety: 'Shift assignment safety',
  attendance_timesheet_readiness: 'Attendance and timesheets',
  basic_audit_trail: 'Basic audit trail',
  basic_document_stamping: 'Basic document stamping',
  custom_workspace_branding: 'Custom workspace branding',
  branded_stamps: 'Branded stamps',
  custom_stamp_templates: 'Custom stamp templates',
  client_hub: 'Client hub',
  branded_pdfs_documents: 'Branded PDF documents',
  advanced_evidence_packs: 'Advanced evidence packs',
  advanced_audit_exports: 'Advanced audit exports',
  payroll_and_invoicing: 'Payroll and invoicing',
  multi_site_branch_controls: 'Multi-site branch controls',
  api_integrations: 'API integrations',
  payroll_exports: 'Payroll exports',
};

const FIELD_GROUPS = [
  {
    id: 'brand',
    title: 'Brand and logo',
    icon: Palette,
    description: 'What tenants, workers and generated documents should show for this workspace.',
    fields: [
      { key: 'company_name', label: 'Legal / workspace name', required: true, placeholder: 'e.g. Osabea Healthcare Solutions Ltd' },
      { key: 'tagline', label: 'Workspace tagline', placeholder: 'e.g. Safe staffing and care compliance' },
      { key: 'logo_url', label: 'Tenant logo', required: true, placeholder: 'Upload PNG or JPEG', inputType: 'logo_upload' },
      { key: 'primary_color', label: 'Primary brand colour', placeholder: '#0f766e', inputType: 'color' },
      { key: 'support_email', label: 'Support email', placeholder: 'support@example.com' },
      { key: 'support_phone', label: 'Support phone', placeholder: '01622 000000', inputType: 'phone' },
    ],
  },
  {
    id: 'company',
    title: 'Company details',
    icon: Building2,
    description: 'Core legal and care-provider information used across onboarding and records.',
    fields: [
      { key: 'company_address', label: 'Company address', required: true, placeholder: 'Suite, Street, City, Postcode', multiline: true },
      { key: 'registered_manager_name', label: 'Registered manager name', placeholder: 'e.g. Jane Smith' },
      { key: 'registered_manager_email', label: 'Registered manager email', placeholder: 'manager@example.com', inputType: 'email' },
      { key: 'main_office_contact', label: 'Main office contact', placeholder: 'Address, phone or main office route workers should use', multiline: true },
      { key: 'website', label: 'Website', placeholder: 'https://example.com' },
      {
        key: 'on_call_contact',
        label: 'Out-of-hours / on-call contact',
        placeholder: 'e.g. 07700 900123, on call Mon-Sun 17:00-08:00',
        multiline: true,
        hint: 'Printed on worker employment documents where emergency escalation details are needed.',
      },
      { key: 'company_registration_number', label: 'Company registration number', placeholder: '12345678', inputType: 'company_number' },
      { key: 'vat_number', label: 'VAT registration number', placeholder: 'GB123456789', inputType: 'vat_number' },
      {
        key: 'company_about',
        label: 'About the organisation',
        required: true,
        placeholder: 'Describe what this organisation is and who it serves.',
        multiline: true,
        hint: 'Used in the Employee Handbook. Use real tenant-specific wording, not generic care text.',
      },
      {
        key: 'service_description',
        label: 'Services provided',
        required: true,
        placeholder: 'Describe the actual services this tenant provides.',
        multiline: true,
      },
      {
        key: 'company_values',
        label: 'Company values',
        required: true,
        placeholder: 'e.g. dignity, compassion, safety, accountability',
        multiline: true,
      },
      {
        key: 'aims_objectives',
        label: 'Aims and objectives',
        required: true,
        placeholder: 'What the organisation aims to achieve for people using services and staff.',
        multiline: true,
      },
      {
        key: 'staff_structure_summary',
        label: 'Staff and management structure',
        required: true,
        placeholder: 'Explain reporting lines, managers and escalation routes.',
        multiline: true,
      },
      { key: 'company_history', label: 'Company history', placeholder: 'Optional short history of the organisation.', multiline: true },
    ],
  },
  {
    id: 'contracts',
    title: 'Role pay defaults',
    icon: FileCheck2,
    description: 'Tenant default worker pay rates used when a worker does not have their own override.',
    fields: [
      {
        key: 'default_hca_hourly_rate',
        label: 'Healthcare Assistant pay rate (GBP/hr)',
        required: true,
        placeholder: '12.98',
        inputType: 'number',
        min: '0',
        step: '0.01',
        hint: 'Used for Healthcare Assistant payroll if the worker does not have a personal pay rate.',
      },
      {
        key: 'default_nurse_hourly_rate',
        label: 'Nurse pay rate (GBP/hr)',
        required: true,
        placeholder: '18.50',
        inputType: 'number',
        min: '0',
        step: '0.01',
        hint: 'Used for Nurse payroll if the worker does not have a personal pay rate.',
      },
      {
        key: 'default_sleep_in_rate',
        label: 'Default sleep-in pay rate (GBP)',
        placeholder: 'Leave blank if no default rate is offered',
        inputType: 'number',
        min: '0',
        step: '0.01',
        hint: 'No £40 or other flat rate is assumed. If sleep-ins are used, enter the real contractual rate; National Minimum Wage rules still apply where relevant.',
      },
      { key: 'payroll_frequency', label: 'Payroll frequency', placeholder: 'Select frequency', inputType: 'select', options: PAYROLL_FREQUENCY_OPTIONS },
      { key: 'payroll_day', label: 'Payroll day', placeholder: 'Select day', inputType: 'select', options: PAYROLL_DAY_OPTIONS },
      { key: 'mileage_rate', label: 'Mileage rate (GBP/mile)', placeholder: '0.45', inputType: 'number', min: '0', step: '0.01' },
      { key: 'mileage_submission_deadline', label: 'Mileage claim deadline', placeholder: 'e.g. Submit by the 20th of each month', multiline: true },
      { key: 'default_contract_start_date', label: 'Default contract start date', placeholder: 'YYYY-MM-DD', inputType: 'date' },
    ],
  },
  {
    id: 'employment_documents',
    title: 'Employment document wording',
    icon: FileCheck2,
    description: 'Tenant-specific details used to make contracts and the handbook read like finished company documents.',
    fields: [
      { key: 'probation_period', label: 'Probation period', placeholder: 'e.g. 6 months' },
      {
        key: 'place_of_work_summary',
        label: 'Place of work wording',
        placeholder: 'e.g. Company premises and client/service locations notified for each accepted assignment.',
        multiline: true,
      },
      { key: 'worker_notice_period', label: 'Worker notice period', placeholder: 'e.g. 1 week' },
      {
        key: 'employer_notice_period',
        label: 'Employer notice wording',
        placeholder: 'e.g. Statutory minimum notice based on continuous service.',
        multiline: true,
      },
      {
        key: 'mandatory_training_summary',
        label: 'Compulsory training wording',
        placeholder: 'Describe induction, mandatory training and any cost/repayment arrangements.',
        multiline: true,
      },
      {
        key: 'other_paid_leave_summary',
        label: 'Other paid leave',
        placeholder: 'Describe statutory or enhanced paid leave offered by this tenant.',
        multiline: true,
      },
      {
        key: 'other_benefits',
        label: 'Other benefits',
        placeholder: 'List genuine benefits only; leave blank if none are provided.',
        multiline: true,
        hint: 'Leaving this blank does not invent a benefit. The contract only treats benefits as contractual when they are expressly confirmed.',
      },
      { key: 'shadowing_duration', label: 'Shadowing arrangement', placeholder: 'e.g. Minimum 3 supervised shifts or risk-based arrangement', multiline: true },
      {
        key: 'occupational_health_provider',
        label: 'Occupational health provider / route',
        placeholder: 'Provider name or referral route',
        hint: 'Leave blank if there is no named provider. The handbook will use neutral referral wording instead of inventing one.',
      },
      { key: 'employee_assistance_details', label: 'Employee assistance / wellbeing support', placeholder: 'Describe genuine support available to staff.', multiline: true },
      { key: 'grievance_contact_name', label: 'Grievance contact', placeholder: 'Name or role' },
      { key: 'grievance_contact_email', label: 'Grievance contact email', placeholder: 'hr@example.com', inputType: 'email' },
      { key: 'appeal_contact_name', label: 'Appeal contact', placeholder: 'Name or role' },
      { key: 'appeal_contact_email', label: 'Appeal contact email', placeholder: 'appeals@example.com', inputType: 'email' },
      { key: 'hr_contact_name', label: 'HR contact', placeholder: 'Name, team or role' },
      { key: 'hr_contact_email', label: 'HR contact email', placeholder: 'hr@example.com', inputType: 'email' },
    ],
  },
  {
    id: 'invoicing',
    title: 'Invoice defaults',
    icon: Receipt,
    description: 'Supplier details printed on client invoices and finance exports.',
    fields: [
      {
        key: 'invoice_payment_terms',
        label: 'Invoice payment terms',
        placeholder: 'Payment due within 30 days of invoice date.',
        multiline: true,
      },
      {
        key: 'invoice_bank_account_name',
        label: 'Account name',
        placeholder: 'e.g. Osabea Healthcare Solutions Ltd',
        required: true,
        inputType: 'bank_account_name',
      },
      {
        key: 'invoice_bank_name',
        label: 'Bank name',
        placeholder: 'e.g. Barclays',
        required: true,
        inputType: 'bank_name',
      },
      {
        key: 'invoice_sort_code',
        label: 'Sort code',
        placeholder: '00-00-00',
        required: true,
        inputType: 'sort_code',
      },
      {
        key: 'invoice_account_number',
        label: 'Account number',
        placeholder: '12345678',
        required: true,
        inputType: 'account_number',
      },
    ],
  },
];

const ALL_FIELDS = FIELD_GROUPS.flatMap((group) => group.fields.map((field) => ({ ...field, groupId: group.id })));
const REQUIRED_FIELDS = ALL_FIELDS.filter((field) => field.required);
const DOCUMENT_READY_FIELDS = [
  'company_name',
  'company_address',
  'logo_url',
  'default_hca_hourly_rate',
  'default_nurse_hourly_rate',
  'payroll_frequency',
  'payroll_day',
  'company_about',
  'service_description',
  'company_values',
  'aims_objectives',
  'staff_structure_summary',
];
const CONTACT_READY_FIELDS = ['support_email', 'support_phone', 'on_call_contact'];

function fieldIsBlank(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

function displayValue(value, required = false) {
  if (fieldIsBlank(value)) return required ? 'Not set - required' : 'Not set';
  return String(value);
}

function onlyDigits(value, maxLength) {
  return String(value || '').replace(/\D/g, '').slice(0, maxLength);
}

function formatSortCode(value) {
  const digits = onlyDigits(value, 6);
  return digits.match(/.{1,2}/g)?.join('-') || '';
}

function formatCompanyNumber(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
}

function formatVatNumber(value) {
  const raw = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const withoutPrefix = raw.startsWith('GB') ? raw.slice(2) : raw;
  const digits = withoutPrefix.replace(/\D/g, '').slice(0, 12);
  return digits ? `GB${digits}` : '';
}

function formatPhone(value) {
  return String(value || '').replace(/[^0-9+()\s.-]/g, '').replace(/\s+/g, ' ').slice(0, 25);
}

function formatLimit(value) {
  if (value === null || value === undefined || value === '') return 'Not set';
  return Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-GB') : String(value);
}

function setupItem(id, label, description, done) {
  return { id, label, description, done };
}

export default function SettingsPage() {
  const { user, token, authHelpers } = useAuth();
  const { updateOrgSettings } = useOrg();
  const [orgSettings, setOrgSettings] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const editInputRefs = useRef({});
  const logoFileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const entitlements = user?.workspace_entitlements || {};
  const limits = entitlements.limits || {};
  const features = entitlements.features || {};
  const planId = entitlements.plan_id || 'private_beta';
  const planLabel = PLAN_LABELS[planId] || String(planId).replace(/_/g, ' ');
  const limitOverride = entitlements.limit_override || {};
  const limitSource = limitOverride.applied
    ? {
        label: 'Custom limits active',
        detail: limitOverride.reason || 'CareOps Admin has applied workspace-specific limits.',
        className: 'border-blue-200 bg-blue-50 text-blue-800',
      }
    : limitOverride.ignored
      ? {
          label: 'Plan limits restored',
          detail: 'An old private-beta override was ignored because it no longer matches this plan.',
          className: 'border-green-200 bg-green-50 text-green-800',
        }
      : {
          label: 'Plan limits',
          detail: `${planLabel} subscription limits are being used.`,
          className: 'border-[#E4E8EB] bg-[#F8FAFA] text-text-muted',
        };
  const roleLabel = ROLE_LABELS[user?.role] || user?.role?.replace(/_/g, ' ') || 'User';
  const logoUrl = orgSettings?.logo_url || '';
  const primaryColor = orgSettings?.primary_color || '#0f766e';

  const loadOrgSettings = async () => {
    setLoadingOrg(true);
    try {
      const res = await axios.get(`${API}/org-settings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setOrgSettings(res.data || {});
    } catch (err) {
      console.error('Failed to load org settings', err);
      toast.error('Could not load workspace settings');
      setOrgSettings({});
    } finally {
      setLoadingOrg(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadOrgSettings();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const readiness = useMemo(() => {
    const apiReadiness = orgSettings?.launch_readiness;
    if (apiReadiness?.checks?.length) {
      return {
        items: apiReadiness.checks,
        complete: apiReadiness.complete || 0,
        total: apiReadiness.total || apiReadiness.checks.length,
        missingRequired: apiReadiness.missing_fields || [],
        blockedCapabilities: apiReadiness.blocked_capabilities || [],
        isLaunchReady: !!apiReadiness.is_launch_ready,
        message: apiReadiness.message,
        source: 'api',
      };
    }

    const missingRequired = REQUIRED_FIELDS.filter((field) => fieldIsBlank(orgSettings?.[field.key]));
    const missingDocument = DOCUMENT_READY_FIELDS.filter((key) => fieldIsBlank(orgSettings?.[key]));
    const missingContact = CONTACT_READY_FIELDS.filter((key) => fieldIsBlank(orgSettings?.[key]));
    const brandComplete = !fieldIsBlank(orgSettings?.company_name) && !fieldIsBlank(orgSettings?.logo_url);
    const billingReady = !fieldIsBlank(orgSettings?.invoice_payment_terms)
      && !fieldIsBlank(orgSettings?.invoice_bank_account_name)
      && !fieldIsBlank(orgSettings?.invoice_bank_name)
      && !fieldIsBlank(orgSettings?.invoice_sort_code)
      && !fieldIsBlank(orgSettings?.invoice_account_number);

    const items = [
      setupItem(
        'company',
        'Company profile',
        'Name and registered address are needed before reliable tenant documents are issued.',
        missingRequired.filter((field) => ['company_name', 'company_address'].includes(field.key)).length === 0,
      ),
      setupItem(
        'documents',
        'Contracts and PDFs',
        'HCA/Nurse pay defaults, company name and address must be complete before contract generation is safe.',
        missingDocument.length === 0,
      ),
      setupItem(
        'brand',
        'Logo and brand',
        'Logo and brand colour must be set before the tenant is fully live.',
        brandComplete,
      ),
      setupItem(
        'contacts',
        'Support and on-call contacts',
        'Workers need the correct support and escalation routes before onboarding starts.',
        missingContact.length === 0,
      ),
      setupItem(
        'billing',
        'Invoice defaults',
        'Payment terms and bank details should be set before invoices are sent from the platform.',
        billingReady,
      ),
      setupItem(
        'subscription',
        'Subscription visible',
        'Tenant can see current plan, limits and feature boundaries. Stripe self-service can be added later.',
        Boolean(entitlements.plan_id),
      ),
    ];

    return {
      items,
      complete: items.filter((item) => item.done).length,
      total: items.length,
      missingRequired,
      blockedCapabilities: [],
      isLaunchReady: items.every((item) => item.done),
      message: items.every((item) => item.done)
        ? 'Workspace setup is complete.'
        : 'Finish workspace setup before tenant launch and official document generation.',
      source: 'local',
    };
  }, [orgSettings, entitlements.plan_id]);

  const includedFeatures = Object.entries(features)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => FEATURE_LABELS[key] || key.replace(/_/g, ' '));
  const upgradeFeatures = Object.entries(features)
    .filter(([, enabled]) => enabled === false)
    .map(([key]) => FEATURE_LABELS[key] || key.replace(/_/g, ' '));
  const accessSummary = getPortalAccessSummary(user, authHelpers?.() || {});
  const readinessById = useMemo(
    () => Object.fromEntries((readiness.items || []).map((item) => [item.id, item])),
    [readiness.items],
  );

  const statusTone = (done) => (
    done
      ? 'border-green-200 bg-green-50 text-green-800'
      : 'border-amber-200 bg-amber-50 text-amber-800'
  );

  const summaryChecks = [
    {
      label: 'Workspace identity',
      ready: Boolean(readinessById.company?.done && readinessById.brand?.done),
      detail: readinessById.brand?.done ? 'Logo and legal details ready' : 'Logo or legal details still need attention',
    },
    {
      label: 'Worker onboarding',
      ready: Boolean(
        readinessById.contacts?.done
        && (readinessById.contract_defaults?.done || readinessById.documents?.done)
        && (readinessById.employment_documents?.done ?? true)
      ),
      detail: readinessById.contacts?.done ? 'Contacts and employment-document content recorded' : 'Support or on-call details incomplete',
    },
    {
      label: 'Documents and PDFs',
      ready: Boolean(
        readinessById.brand?.done
        && (readinessById.contract_defaults?.done || readinessById.documents?.done)
        && (readinessById.employment_documents?.done ?? true)
      ),
      detail: (readinessById.contract_defaults?.done || readinessById.documents?.done)
        ? 'Branding, rates and tenant-specific employment content checked'
        : 'Rates, payroll defaults or employment content incomplete',
    },
    {
      label: 'Finance setup',
      ready: Boolean(readinessById.billing?.done),
      detail: readinessById.billing?.done ? 'Invoice defaults ready' : 'Invoice terms or bank details incomplete',
    },
  ];

  const openEdit = () => {
    const initial = {};
    ALL_FIELDS.forEach((field) => {
      const value = orgSettings?.[field.key];
      initial[field.key] = value === null || value === undefined ? '' : String(value);
    });
    setForm(initial);
    setEditOpen(true);
  };

  const handleSave = async () => {
    const blankRequired = REQUIRED_FIELDS.filter((field) => fieldIsBlank(form[field.key]));
    if (blankRequired.length) {
      toast.error(`Required: ${blankRequired.map((field) => field.label).join(', ')}`);
      return;
    }
    if (onlyDigits(form.invoice_sort_code, 99).length !== 6) {
      toast.error('Sort code must be 6 digits');
      return;
    }
    if (onlyDigits(form.invoice_account_number, 99).length !== 8) {
      toast.error('Account number must be 8 digits');
      return;
    }
    if (form.company_registration_number && !/^([A-Z]{2}\d{6}|\d{8})$/.test(formatCompanyNumber(form.company_registration_number))) {
      toast.error('Company number must be 8 digits, or a UK prefix plus 6 digits');
      return;
    }
    if (form.vat_number && !/^GB\d{9}(\d{3})?$/.test(formatVatNumber(form.vat_number))) {
      toast.error('VAT number must look like GB123456789');
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      ALL_FIELDS.forEach((field) => {
        const value = form[field.key];
        if (field.inputType === 'sort_code') {
          payload[field.key] = formatSortCode(value);
        } else if (field.inputType === 'account_number') {
          payload[field.key] = onlyDigits(value, 8);
        } else if (field.inputType === 'company_number') {
          payload[field.key] = formatCompanyNumber(value);
        } else if (field.inputType === 'vat_number') {
          payload[field.key] = formatVatNumber(value);
        } else if (field.inputType === 'phone') {
          payload[field.key] = formatPhone(value);
        } else {
          payload[field.key] = typeof value === 'string' ? value.trim() : value;
        }
      });

      const result = await updateOrgSettings(payload);
      if (!result?.success) {
        throw new Error(typeof result?.error === 'string' ? result.error : 'Failed to save workspace settings');
      }
      setOrgSettings(result.data || orgSettings);
      toast.success('Workspace settings saved');
      setEditOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        detail?.message ||
        err?.message ||
        'Failed to save settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Logo must be a PNG or JPEG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be 2MB or smaller');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setLogoUploading(true);
    try {
      const res = await axios.post(`${API}/org-settings/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const updated = res.data || {};
      setOrgSettings(updated);
      setForm((prev) => ({ ...prev, logo_url: updated.logo_url || prev.logo_url || '' }));
      toast.success('Tenant logo uploaded');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === 'string' && detail) || detail?.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const renderReadinessPill = () => {
    if (!orgSettings) return null;
    const ready = readiness.isLaunchReady;
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
          ready ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
        }`}
        data-testid={ready ? 'org-settings-readiness-ok' : 'org-settings-readiness-missing'}
      >
        {ready ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
        {ready ? 'Launch ready' : `Setup missing (${readiness.missingRequired.length})`}
      </span>
    );
  };

  const renderField = (field) => {
    const value = orgSettings?.[field.key];
    const blank = fieldIsBlank(value);
    return (
      <div key={field.key} className="border-b border-[#F0F2F4] py-2 last:border-0" data-testid={`org-field-${field.key}`}>
        <p className="flex items-center gap-1.5 text-xs text-text-muted">
          {field.label}
          {field.required ? <span className="text-red-500">*</span> : null}
        </p>
        {field.hint ? <p className="mt-0.5 text-[11px] leading-snug text-text-muted">{field.hint}</p> : null}
        <p className={`mt-1 text-sm font-medium ${blank ? (field.required ? 'text-red-600' : 'text-text-muted italic') : 'text-text-primary'}`}>
          {displayValue(value, field.required)}
        </p>
      </div>
    );
  };

  const renderEditControl = (field) => {
    if (field.multiline) {
      return (
        <textarea
          id={`org-${field.key}`}
          rows={3}
          placeholder={field.placeholder}
          value={form[field.key] ?? ''}
          onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`org-input-${field.key}`}
        />
      );
    }

    if (field.inputType === 'select') {
      return (
        <select
          id={`org-${field.key}`}
          value={form[field.key] ?? ''}
          onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`org-input-${field.key}`}
        >
          <option value="">{field.placeholder || 'Select...'}</option>
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.inputType === 'logo_upload') {
      const currentLogo = form[field.key] || orgSettings?.[field.key] || '';
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#DDE5E8] bg-[#F8FAFA] p-3">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-[#C9D6DA] bg-white">
              {currentLogo ? (
                <img src={currentLogo} alt="Tenant logo preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <Image className="h-7 w-7 text-text-muted" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-text-primary">Upload tenant logo</p>
              <p className="text-xs text-text-muted">PNG or JPEG, up to 2MB. This feeds the workspace header, emails, PDFs and stamps.</p>
              {currentLogo ? <p className="mt-1 truncate text-[11px] text-text-muted">{currentLogo}</p> : null}
            </div>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              disabled={logoUploading}
              onClick={() => logoFileInputRef.current?.click()}
            >
              {logoUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {logoUploading ? 'Uploading' : 'Choose logo'}
            </Button>
            <input
              ref={logoFileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleLogoFileSelected}
              data-testid={`org-input-${field.key}`}
            />
          </div>
          <Input
            id={`org-${field.key}`}
            type="url"
            placeholder="Uploaded logo URL"
            value={currentLogo}
            readOnly
            className="bg-muted/40 text-xs"
            aria-label="Uploaded tenant logo URL"
          />
        </div>
      );
    }

    if (field.inputType === 'color') {
      return (
        <div className="flex gap-2">
          <Input
            id={`org-${field.key}`}
            placeholder={field.placeholder}
            value={form[field.key] ?? ''}
            onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
            data-testid={`org-input-${field.key}`}
          />
          <Input
            type="color"
            aria-label="Choose primary colour"
            className="h-10 w-12 p-1"
            value={/^#[0-9A-Fa-f]{6}$/.test(form[field.key] || '') ? form[field.key] : '#0f766e'}
            onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          />
        </div>
      );
    }

    if (field.inputType === 'sort_code') {
      return (
        <Input
          id={`org-${field.key}`}
          ref={(node) => { editInputRefs.current[field.key] = node; }}
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          placeholder={field.placeholder}
          value={formatSortCode(form[field.key] ?? '')}
          onChange={(e) => {
            const formatted = formatSortCode(e.target.value);
            setForm({ ...form, [field.key]: formatted });
            if (onlyDigits(formatted, 6).length === 6) {
              window.setTimeout(() => editInputRefs.current.invoice_account_number?.focus(), 0);
            }
          }}
          data-testid={`org-input-${field.key}`}
        />
      );
    }

    if (field.inputType === 'account_number') {
      return (
        <Input
          id={`org-${field.key}`}
          ref={(node) => { editInputRefs.current[field.key] = node; }}
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          placeholder={field.placeholder}
          value={onlyDigits(form[field.key] ?? '', 8)}
          onChange={(e) => setForm({ ...form, [field.key]: onlyDigits(e.target.value, 8) })}
          data-testid={`org-input-${field.key}`}
        />
      );
    }

    if (field.inputType === 'company_number') {
      return (
        <Input
          id={`org-${field.key}`}
          autoComplete="off"
          maxLength={8}
          placeholder={field.placeholder}
          value={formatCompanyNumber(form[field.key] ?? '')}
          onChange={(e) => setForm({ ...form, [field.key]: formatCompanyNumber(e.target.value) })}
          data-testid={`org-input-${field.key}`}
        />
      );
    }

    if (field.inputType === 'vat_number') {
      return (
        <Input
          id={`org-${field.key}`}
          autoComplete="off"
          maxLength={14}
          placeholder={field.placeholder}
          value={formatVatNumber(form[field.key] ?? '')}
          onChange={(e) => setForm({ ...form, [field.key]: formatVatNumber(e.target.value) })}
          data-testid={`org-input-${field.key}`}
        />
      );
    }

    if (field.inputType === 'phone') {
      return (
        <Input
          id={`org-${field.key}`}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          maxLength={25}
          placeholder={field.placeholder}
          value={formatPhone(form[field.key] ?? '')}
          onChange={(e) => setForm({ ...form, [field.key]: formatPhone(e.target.value) })}
          data-testid={`org-input-${field.key}`}
        />
      );
    }

    return (
      <Input
        id={`org-${field.key}`}
        type={field.inputType || 'text'}
        min={field.min}
        step={field.step}
        placeholder={field.placeholder}
        value={form[field.key] ?? ''}
        onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
        data-testid={`org-input-${field.key}`}
      />
    );
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-primary">Tenant workspace setup</p>
          <h1 className="text-3xl font-heading font-semibold text-text-primary">Workspace Settings</h1>
          <p className="mt-1 max-w-3xl text-text-muted">
            Complete this first. These details control the tenant dashboard, worker onboarding, contracts, stamps, PDFs and invoices.
          </p>
        </div>
        {isAdmin ? (
          <Button onClick={openEdit} disabled={loadingOrg} className="rounded-lg" data-testid="org-settings-edit-btn">
            <Pencil className="mr-2 h-4 w-4" />
            Edit setup
          </Button>
        ) : null}
      </div>

      <MfaSecurityCard />

      {!isAdmin ? (
        <Card className="border-amber-200 bg-amber-50 shadow-sm">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
            <div>
              <p className="font-semibold text-amber-900">Workspace setup is admin-only</p>
              <p className="text-sm text-amber-800">
                Ask your workspace owner to update organisation, contract and billing defaults.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {isAdmin && (
        <>
          {!loadingOrg && !readiness.isLaunchReady ? (
            <Card className="border-amber-200 bg-amber-50 shadow-sm" data-testid="tenant-launch-hold-banner">
              <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
                  <div>
                    <p className="font-semibold text-amber-950">Tenant launch hold: setup is not complete</p>
                    <p className="mt-1 text-sm text-amber-900">
                      {readiness.message || 'Finish workspace setup before tenant launch and official document generation.'}
                    </p>
                    {readiness.missingRequired.length ? (
                      <p className="mt-2 text-xs text-amber-800">
                        Missing: {readiness.missingRequired.slice(0, 6).map((field) => field.label || field.key || field).join(', ')}
                        {readiness.missingRequired.length > 6 ? ` and ${readiness.missingRequired.length - 6} more` : ''}.
                      </p>
                    ) : null}
                  </div>
                </div>
                <Button onClick={openEdit} variant="outline" className="shrink-0 rounded-lg border-amber-300 bg-white text-amber-900 hover:bg-amber-100">
                  Fix setup
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-[1.4fr_0.9fr]">
            <Card className="border-[#E4E8EB] shadow-sm">
              <CardContent className="p-5">
                {loadingOrg ? (
                  <div className="flex items-center gap-2 py-6 text-sm text-text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading workspace setup...
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="flex flex-wrap items-center gap-5">
                      <div
                        className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-[#DDE6EA] bg-[#F8FAFA]"
                        style={{ borderColor: primaryColor }}
                      >
                        {logoUrl ? (
                          <img src={logoUrl} alt={orgSettings?.organisation_name || 'Workspace logo'} className="h-full w-full object-contain p-2" />
                        ) : (
                          <Image className="h-8 w-8 text-text-muted" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="truncate text-2xl font-heading font-semibold text-text-primary">
                            {orgSettings?.organisation_name || orgSettings?.company_name || 'Workspace'}
                          </h2>
                          {renderReadinessPill()}
                        </div>
                        <p className="mt-1 text-sm text-text-muted">{orgSettings?.tagline || 'No workspace tagline set yet.'}</p>
                        <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                          <span className="rounded-lg bg-[#F8FAFA] px-3 py-2 text-text-muted">
                            Plan: <strong className="text-text-primary">{planLabel}</strong>
                          </span>
                          <span className="rounded-lg bg-[#F8FAFA] px-3 py-2 text-text-muted">
                            Your role: <strong className="text-text-primary capitalize">{roleLabel}</strong>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-[#E4E8EB] bg-[#F8FAFA] p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Workspace control summary</p>
                          <p className="mt-1 text-sm text-text-muted">
                            Permanent health view for branding, onboarding, document generation and finance defaults.
                          </p>
                        </div>
                        <Button onClick={openEdit} variant="outline" size="sm" className="rounded-lg">
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit setup
                        </Button>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        {summaryChecks.map((item) => (
                          <div key={item.label} className={`rounded-lg border px-3 py-2 ${statusTone(item.ready)}`}>
                            <div className="flex items-start gap-2">
                              {item.ready ? (
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                              ) : (
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                              )}
                              <div>
                                <p className="text-sm font-semibold">{item.label}</p>
                                <p className="text-xs leading-snug opacity-80">{item.detail}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="rounded-xl border border-[#E4E8EB] p-3">
                        <p className="text-xs font-semibold uppercase text-text-muted">Support</p>
                        <p className="mt-1 truncate text-sm font-semibold text-text-primary">{displayValue(orgSettings?.support_email)}</p>
                        <p className="truncate text-xs text-text-muted">{displayValue(orgSettings?.support_phone)}</p>
                      </div>
                      <div className="rounded-xl border border-[#E4E8EB] p-3">
                        <p className="text-xs font-semibold uppercase text-text-muted">On-call</p>
                        <p className={`mt-1 truncate text-sm font-semibold ${fieldIsBlank(orgSettings?.on_call_contact) ? 'text-amber-700' : 'text-text-primary'}`}>
                          {displayValue(orgSettings?.on_call_contact)}
                        </p>
                        <p className="text-xs text-text-muted">Shown on worker documents</p>
                      </div>
                      <div className="rounded-xl border border-[#E4E8EB] p-3">
                        <p className="text-xs font-semibold uppercase text-text-muted">Payroll cycle</p>
                        <p className={`mt-1 truncate text-sm font-semibold ${fieldIsBlank(orgSettings?.payroll_frequency) || fieldIsBlank(orgSettings?.payroll_day) ? 'text-amber-700' : 'text-text-primary'}`}>
                          {fieldIsBlank(orgSettings?.payroll_frequency) && fieldIsBlank(orgSettings?.payroll_day)
                            ? 'Not set'
                            : `${displayValue(orgSettings?.payroll_frequency)} / ${displayValue(orgSettings?.payroll_day)}`}
                        </p>
                        <p className="text-xs text-text-muted">Feeds timesheets and payroll</p>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-4">
                      {[
                        ['Admin seats', limits.admin_users_limit],
                        ['Workers', limits.workers_limit],
                        ['Service users', limits.service_users_limit],
                        ['Applicant invites / month', limits.applicant_invites_monthly_limit],
                        ['Storage GB', limits.storage_gb_limit],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-lg bg-[#F8FAFA] px-3 py-2">
                          <p className="text-[11px] uppercase text-text-muted">{label}</p>
                          <p className="text-lg font-semibold text-text-primary">{formatLimit(value)}</p>
                        </div>
                      ))}
                    </div>
                    <div className={`rounded-lg border px-3 py-2 text-xs ${limitSource.className}`}>
                      <span className="font-semibold">{limitSource.label}</span>
                      <span className="ml-2">{limitSource.detail}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#E4E8EB] shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  Setup readiness
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 flex items-end justify-between">
                  <div>
                    <p className="text-3xl font-semibold text-text-primary">{readiness.complete}/{readiness.total}</p>
                    <p className="text-sm text-text-muted">launch checks complete</p>
                  </div>
                  <div className="h-2 w-32 rounded-full bg-slate-100">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${readiness.total ? (readiness.complete / readiness.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  {readiness.items.map((item) => (
                    <div key={item.id} className="flex items-start gap-2 rounded-lg border border-[#E4E8EB] p-3">
                      {item.done ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                      )}
                      <div>
                        <p className="text-sm font-semibold text-text-primary">{item.label}</p>
                        <p className="text-xs leading-snug text-text-muted">{item.description}</p>
                        {!item.done && item.missing_fields?.length ? (
                          <p className="mt-1 text-xs font-medium text-amber-700">
                            Missing: {item.missing_fields.map((field) => field.label || field.key).join(', ')}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {readiness.blockedCapabilities.length ? (
              <Card className="border-amber-200 bg-amber-50 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg font-heading text-amber-950">
                    <Shield className="h-5 w-5" />
                    Setup currently affects
                  </CardTitle>
                  <p className="text-sm text-amber-900">
                    These areas are blocked by tenant setup data returned from the system.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {readiness.blockedCapabilities.map((capability) => (
                    <span key={capability} className="rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800">
                      {CAPABILITY_LABELS[capability] || capability.replace(/_/g, ' ')}
                    </span>
                  ))}
                </CardContent>
              </Card>
            ) : null}
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            {FIELD_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              return (
                <Card key={group.id} className="border-[#E4E8EB] shadow-sm" data-testid={`settings-group-${group.id}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-heading">
                      <GroupIcon className="h-5 w-5 text-primary" />
                      {group.title}
                    </CardTitle>
                    <p className="text-sm text-text-muted">{group.description}</p>
                  </CardHeader>
                  <CardContent className="grid gap-x-8 sm:grid-cols-2">
                    {group.fields.map(renderField)}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid gap-6 xl:grid-cols-[0.85fr_1.15fr]">
            <Card className="border-[#E4E8EB] shadow-sm" data-testid="subscription-summary-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Subscription
                </CardTitle>
                <p className="text-sm text-text-muted">
                  Billing is owner-managed during beta. Stripe self-service can be switched on after the tenant journey is stable.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <p className="text-xs font-semibold uppercase text-primary">Current plan</p>
                  <p className="mt-1 text-2xl font-semibold text-text-primary">{planLabel}</p>
                  <p className="mt-1 text-sm text-text-muted">Plan changes are currently controlled by CareOps Admin.</p>
                  <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${limitSource.className}`}>
                    <span className="font-semibold">{limitSource.label}</span>
                    <span className="ml-2">{limitSource.detail}</span>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ['Admin seats', limits.admin_users_limit],
                    ['Workers', limits.workers_limit],
                    ['Service users', limits.service_users_limit],
                    ['Client organisations', limits.client_organisations_limit],
                    ['Applicant invites / month', limits.applicant_invites_monthly_limit],
                    ['Storage GB', limits.storage_gb_limit],
                    ['Evidence exports', limits.evidence_pack_exports_limit],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-[#E4E8EB] p-3">
                      <p className="text-xs uppercase text-text-muted">{label}</p>
                      <p className="mt-1 text-xl font-semibold text-text-primary">{formatLimit(value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E4E8EB] shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <Shield className="h-5 w-5 text-primary" />
                  Feature access
                </CardTitle>
                <p className="text-sm text-text-muted">
                  These are enforced by route guards and backend checks where the feature has been connected.
                </p>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-green-700">Included now</p>
                  <div className="flex flex-wrap gap-2">
                    {(includedFeatures.length ? includedFeatures : ['No feature data available']).map((label) => (
                      <span key={label} className="rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs text-green-800">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Available as upgrade</p>
                  <div className="flex flex-wrap gap-2">
                    {(upgradeFeatures.length ? upgradeFeatures : ['No locked features listed']).map((label) => (
                      <span key={label} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-[#E4E8EB] shadow-sm" data-testid="workspace-access-map-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <Shield className="h-5 w-5 text-primary" />
                Workspace access map
              </CardTitle>
              <p className="text-sm text-text-muted">
                This mirrors the sidebar and direct-link guard, so tenants only see areas their role and subscription can actually use.
              </p>
            </CardHeader>
            <CardContent className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
              <div>
                <p className="mb-3 text-xs font-semibold uppercase text-green-700">Available in this workspace</p>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {accessSummary.availableSections.map((section) => (
                    <div key={section.id} className="rounded-xl border border-[#E4E8EB] bg-[#F8FAFA] p-3">
                      <p className="text-sm font-semibold text-text-primary">{section.label}</p>
                      <p className="mt-0.5 text-xs text-text-muted">{section.description}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {section.items.map((item) => (
                          <span key={item.href} className="group relative rounded-full border border-green-200 bg-white px-2 py-1 text-[11px] font-medium text-green-800">
                            {item.name}
                            {item.enforcement?.length ? (
                              <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-64 rounded-lg border border-slate-200 bg-white p-2 text-left text-[11px] leading-4 text-slate-600 shadow-lg group-hover:block">
                                {item.enforcement.join(' | ')}
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-xs font-semibold uppercase text-slate-500">Locked or kept out of tenant view</p>
                <div className="space-y-2">
                  {accessSummary.locked.length ? accessSummary.locked.map((item) => (
                    <div key={`${item.section}-${item.name}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-text-primary">{item.name}</p>
                          <p className="mt-0.5 text-xs text-text-muted">{item.reason || 'Not included in this plan.'}</p>
                          {item.enforcement?.length ? (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {item.enforcement.slice(0, 3).map((label) => (
                                <span key={label} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                  {label}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {item.upgradePlan ? (
                          <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">
                            {item.upgradePlan}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                      No subscription-locked tenant pages for this account.
                    </div>
                  )}
                  {accessSummary.internal.length ? (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                      {accessSummary.internal.length} internal template/admin tool(s) are intentionally hidden from tenant users during beta.
                    </div>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E4E8EB] shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <User className="h-5 w-5 text-primary" />
                Your access
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-[#F8FAFA] p-3">
                <p className="text-xs text-text-muted">Name</p>
                <p className="truncate text-sm font-semibold text-text-primary">{user?.name || 'Not set'}</p>
              </div>
              <div className="rounded-lg bg-[#F8FAFA] p-3">
                <p className="text-xs text-text-muted">Email</p>
                <p className="truncate text-sm font-semibold text-text-primary">{user?.email || 'Not set'}</p>
              </div>
              <div className="rounded-lg bg-[#F8FAFA] p-3">
                <p className="text-xs text-text-muted">Role</p>
                <p className="truncate text-sm font-semibold text-text-primary capitalize">{roleLabel}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E4E8EB] shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <Bell className="h-5 w-5 text-primary" />
                Alerts and notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-text-muted">
              <p>
                Compliance and staffing alerts appear on the Dashboard task queue and on each worker profile.
                Workers receive updates in their portal notifications panel.
              </p>
              <p>
                Organisation-wide email and contact wording uses the support and on-call details above.
              </p>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]" data-testid="org-settings-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Edit Workspace Setup
            </DialogTitle>
            <DialogDescription>
              Required fields protect tenant identity, contracts and PDF generation. Optional fields can be cleared if they no longer apply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            {FIELD_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              return (
                <section key={group.id} className="rounded-xl border border-[#E4E8EB] p-4">
                  <div className="mb-4 flex items-start gap-2">
                    <GroupIcon className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <h3 className="font-semibold text-text-primary">{group.title}</h3>
                      <p className="text-xs text-text-muted">{group.description}</p>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {group.fields.map((field) => (
                      <div key={field.key} className={field.multiline ? 'space-y-1.5 sm:col-span-2' : 'space-y-1.5'}>
                        <Label htmlFor={`org-${field.key}`} className="text-sm">
                          {field.label}
                          {field.required ? <span className="ml-1 text-red-500">*</span> : null}
                        </Label>
                        {renderEditControl(field)}
                        {field.hint ? <p className="text-xs leading-snug text-text-muted">{field.hint}</p> : null}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving} data-testid="org-settings-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="org-settings-save-btn">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Save workspace setup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
